import useLocalStorage from "use-local-storage";
import { ChangeEvent, Dispatch, JSX, SetStateAction, useRef, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import BrowserUpdatedIcon from "@mui/icons-material/BrowserUpdated";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { DataGrid, GridColDef, GridFilterModel, GridPagination, GridRowModel } from "@mui/x-data-grid";

import {
  ClassroomsNoNulls,
  COMPONENT_CUSTOM_MAXHEIGHT,
  CRPS_ORANGE_DARK_BRIGHT,
  CRPS_ORANGE_LIGHT_BRIGHT,
  EXPORT_COLUMNS_SORTED,
  EXPORT_COLUMNS_STUDENTS,
  Grade,
  GRADES,
  psLocalStorageFilter,
  PsType,
  SetSnackType,
  SettingsGrades,
  SortedWithIntensities,
  STUDENT_ACHIEVEMENTS,
  STUDENT_BEHAVIOURS,
  STUDENT_SOCIALS,
  studentAchievementsDescToIdMap,
  studentBehavioursDescToIdMap,
  StudentConditionsNoNulls,
  StudentsNoNulls,
  studentSocialsDescToIdMap,
  TeacherConditionsNoNulls,
  TeachersNoNulls,
  Upload,
  UPLOAD_COLUMNS_CLASSROOMS,
  UPLOAD_COLUMNS_PSCHOOL_STUDENTS,
  UPLOAD_COLUMNS_STUDENTS,
  UPLOAD_COLUMNS_TEACHERS,
  VClassroomsNoNulls,
  VFailedNoNulls,
  VSortedNoNulls,
  VStudentConditionsNoNulls,
  VStudentsNoNulls,
  VTeacherConditionsNoNulls,
} from ".";

const csvTransform = (type: PsType, data: any[], teachers: TeachersNoNulls[]) => {
  let headers = [] as string[];
  let rows = [] as any[];
  switch (type) {
    case "Classrooms":
      headers = UPLOAD_COLUMNS_CLASSROOMS;
      rows = data.map((c: ClassroomsNoNulls) => [
        c.name,
        teachers.find(t => t.id === c.teacher_id)?.prefix,
        teachers.find(t => t.id === c.teacher_id)?.first,
        teachers.find(t => t.id === c.teacher_id)?.last,
        c.seats0am,
        c.seats0pm,
        c.seats1,
        c.seats2,
        c.seats3,
        c.seats4,
        c.seats5,
        c.seats6,
        c.seats7,
        c.seats8,
        c.seats9,
        c.seats10,
        c.seats11,
        c.seats12,
        c.seats13,
      ]);
      break;
    case "Students":
      headers = EXPORT_COLUMNS_STUDENTS;
      rows = data.map((s: StudentsNoNulls) => [
        s.grade,
        s.last,
        s.first,
        s.number,
        s.gender,
        s.indigenous,
        STUDENT_ACHIEVEMENTS.find(sa => sa.id === s.achievement_id)?.desc,
        STUDENT_BEHAVIOURS.find(sb => sb.id === s.behaviour_id)?.desc,
        STUDENT_SOCIALS.find(ss => ss.id === s.social_id)?.desc,
        s.ell,
        s.sp_ed_1,
        s.sp_ed_2,
        s.sp_ed_3,
        s.spcl_ed_1,
        s.spcl_ed_2,
      ]);
      break;
    case "Teachers":
      headers = UPLOAD_COLUMNS_TEACHERS;
      rows = data.map((t: TeachersNoNulls) => [
        t.prefix,
        t.first,
        t.last,
        t.years,
        t.grade0am,
        t.grade0pm,
        t.grade1,
        t.grade2,
        t.grade3,
        t.grade4,
        t.grade5,
        t.grade6,
        t.grade7,
        t.grade8,
        t.grade9,
        t.grade10,
        t.grade11,
        t.grade12,
        t.grade13,
      ]);
      break;
    default:
      headers = EXPORT_COLUMNS_SORTED;
      rows = data.map((i: SortedWithIntensities) => [
        i.c_name,
        (i.stu_int_ach ?? 0) + (i.stu_int_beh ?? 0) + (i.stu_int_res ?? 0) + (i.stu_int_soc ?? 0),
        i.s_grade,
        i.s_last,
        i.s_first,
        i.s_number,
        i.s_gender,
        i.s_indigenous,
        i.stu_int_ach,
        i.stu_int_beh,
        i.stu_int_soc,
        i.s_ell,
        i.s_sp_ed_1,
        i.s_sp_ed_2,
        i.s_sp_ed_3,
        i.s_spcl_ed_1,
        i.s_spcl_ed_2,
      ]);
  }
  return [headers.join(","), rows.map(r => r.join(","))];
};

const ButtonExportCSV = (props: { data: any[]; psType: PsType; teachers: TeachersNoNulls[] }) => (
  <Button
    component="label"
    variant="outlined"
    onClick={(ev: any) => {
      const [headerRow, dataRows] = csvTransform(props.psType, props.data, props.teachers);
      const url = URL.createObjectURL(
        new Blob([[headerRow, ...dataRows].join("\n")], { type: "text/csv;charset=utf-8;" }),
      );
      const a = document.createElement("a");
      a.setAttribute("href", url);
      a.setAttribute("download", `${yMd_Hms()}_${props.psType}.csv`);
      a.click();
      URL.revokeObjectURL(url);
    }}
    startIcon={<BrowserUpdatedIcon />}
  >
    Export
  </Button>
);

const ButtonImportFile = (props: {
  extension: ".csv" | ".text";
  invalid: any;
  psType: PsType;
  teachers: TeachersNoNulls[];
  uploaded: (rows: Upload[]) => void;
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  return (
    <Button
      color={props.extension === ".text" ? "secondary" : "primary"}
      onClick={() => fileInputRef.current?.click()}
      startIcon={<CloudUploadIcon />}
      variant="contained"
    >
      <input
        accept={props.extension}
        onChange={(ev: ChangeEvent<HTMLInputElement>) => {
          const file = ev.target.files?.[0];
          if (!file) {
            ev.target.value = "";
            return;
          }
          const reader = new FileReader();
          reader.onabort = evA => console.error("upload aborted:", evA);
          reader.onerror = evE => console.error("upload error:", evE);
          reader.onload = evL => {
            const { isValid, reason, uploadedRows } = upload(
              props.extension === ".text" ? "PowerSchoolText" : props.psType,
              reader?.result,
              props.teachers,
            );
            if (!isValid) {
              ev.target.value = "";
              return props.invalid(reason);
            }
            props.uploaded(uploadedRows ?? []);
          };
          try {
            reader.readAsText(file);
          } catch (err: any) {
            console.error("Upload", err);
          } finally {
            ev.target.value = "";
          }
        }}
        ref={fileInputRef}
        type="file"
        style={{ display: "none" }}
      />
      {props.extension === ".text" ? "PSchool .text" : `Import ${props.psType}`}
    </Button>
  );
};

export const ButtonTrashRow = (props: {
  disabled: boolean;
  toggleVisibility: Dispatch<SetStateAction<boolean>>;
  visible: boolean;
}) => (
  <Button
    disabled={props.disabled}
    onClick={ev => props.toggleVisibility(v => !v)}
    startIcon={props.visible ? <DeleteIcon /> : <DeleteOutlineIcon />}
    variant={props.visible ? "contained" : "outlined"}
  >
    Toggle Del
  </Button>
);

export const distinctGrades = (students: StudentsNoNulls[] | VStudentsNoNulls[]): Grade[] =>
  GRADES.filter(g => new Set([...students.map(s => s.grade)]).has(g));

const importClassrooms = (
  hRow: string,
  rows: string[],
  delim: string,
  teachers: TeachersNoNulls[],
): ClassroomsNoNulls[] => {
  const arr = new Array<ClassroomsNoNulls>();
  const headers = hRow.split(delim);
  for (const row of rows) {
    const iCols = row.split(delim);
    const uploadTchP = iCols[headers.indexOf("Teacher Prefix")];
    const uploadTchF = iCols[headers.indexOf("Teacher First")];
    const uploadTchL = iCols[headers.indexOf("Teacher Last")];
    const teacher = teachers.find(
      t => (t.prefix ?? "N/A") === (uploadTchP ?? "N/A") && t.last === uploadTchL && t.first === uploadTchF,
    );
    arr.push({
      name: iCols[headers.indexOf("Name")],
      seats0am: Number(iCols[headers.indexOf("ECS-AM Seats")] ?? 0),
      seats0pm: Number(iCols[headers.indexOf("ECS-PM Seats")] ?? 0),
      seats1: Number(iCols[headers.indexOf("Gr 1 Seats")] ?? 0),
      seats2: Number(iCols[headers.indexOf("Gr 2 Seats")] ?? 0),
      seats3: Number(iCols[headers.indexOf("Gr 3 Seats")] ?? 0),
      seats4: Number(iCols[headers.indexOf("Gr 4 Seats")] ?? 0),
      seats5: Number(iCols[headers.indexOf("Gr 5 Seats")] ?? 0),
      seats6: Number(iCols[headers.indexOf("Gr 6 Seats")] ?? 0),
      seats7: Number(iCols[headers.indexOf("Gr 7 Seats")] ?? 0),
      seats8: Number(iCols[headers.indexOf("Gr 8 Seats")] ?? 0),
      seats9: Number(iCols[headers.indexOf("Gr 9 Seats")] ?? 0),
      seats10: Number(iCols[headers.indexOf("Gr 10 Seats")] ?? 0),
      seats11: Number(iCols[headers.indexOf("Gr 11 Seats")] ?? 0),
      seats12: Number(iCols[headers.indexOf("Gr 12 Seats")] ?? 0),
      seats13: Number(iCols[headers.indexOf("Gr 13 Seats")] ?? 0),
      teacher_id: teacher?.id,
    } as ClassroomsNoNulls);
  }
  return arr.filter(s => !!s.name);
};

const importStudents = (hRow: string, rows: string[], delim: "," | "\t"): StudentsNoNulls[] => {
  const arr = new Array<StudentsNoNulls>();
  const headers = hRow.split(delim);
  for (const row of rows) {
    const iCols = row.split(delim);
    if (delim === "\t") {
      // PSchool .text

      // if "Sched_NextYearGrade" is missing then must "Grade_Level"++
      const gradeCol = headers.includes("Sched_NextYearGrade") ? "Sched_NextYearGrade" : "Grade_Level";
      let gradeNum = Number(iCols[headers.indexOf(gradeCol)]);
      if (!headers.includes("Sched_NextYearGrade")) gradeNum++;
      if (gradeNum > 13) gradeNum = 13;

      // if "State_StudentNumber" is missing then "Student_Number"
      const numCol = headers.includes("State_StudentNumber") ? "State_StudentNumber" : "Student_Number";

      arr.push({
        number: Number(iCols[headers.indexOf(numCol)]),
        grade: gradeNum === 0 ? `ECS-${Math.random() < 0.5 ? "A" : "P"}M` : GRADES[GRADES.indexOf(`Gr ${gradeNum}`)],
        last: iCols[headers.indexOf("Last_Name")],
        first: iCols[headers.indexOf("First_Name")],
        gender: iCols[headers.indexOf("MF")] ?? undefined,
        indigenous: (iCols[headers.indexOf("S_AB_STU_X.FNMI")] ?? "") !== "",
        ell: Number(iCols[headers.indexOf("U_StudentsUserFields.cc_ell_level_current")]),
        sp_ed_1: Number(iCols[headers.indexOf("U_StudentsUserFields.cc_spd_excep_cd_1_detail")]),
        sp_ed_2: Number(iCols[headers.indexOf("U_StudentsUserFields.cc_spd_excep_cd_2_detail")]),
        sp_ed_3: Number(iCols[headers.indexOf("U_StudentsUserFields.cc_spd_excep_cd_3_detail")]),
        spcl_ed_1: Number(iCols[headers.indexOf("AB_spcl_Edu_stu_1")]),
        spcl_ed_2: Number(iCols[headers.indexOf("AB_spcl_Edu_stu_2")]),
        achievement_id: studentAchievementsDescToIdMap.get("None"),
        behaviour_id: studentBehavioursDescToIdMap.get("None"),
        social_id: studentSocialsDescToIdMap.get("None"),
      } as StudentsNoNulls);
    } else {
      // CSV
      arr.push({
        number: Number(iCols[headers.indexOf("ID #")]),
        grade: iCols[headers.indexOf("Grade")],
        last: iCols[headers.indexOf("Last Name")],
        first: iCols[headers.indexOf("First Name")],
        gender: iCols[headers.indexOf("Gender")] ?? undefined,
        indigenous: (iCols[headers.indexOf("Indigenous")] ?? "false").toLowerCase() === "true",
        ell: Number(iCols[headers.indexOf("ELL")]),
        sp_ed_1: Number(iCols[headers.indexOf("SpEd1")]),
        sp_ed_2: Number(iCols[headers.indexOf("SpEd2")]),
        sp_ed_3: Number(iCols[headers.indexOf("SpEd3")]),
        spcl_ed_1: Number(iCols[headers.indexOf("SpClEd1")]),
        spcl_ed_2: Number(iCols[headers.indexOf("SpClEd2")]),
        achievement_id: studentAchievementsDescToIdMap.get(iCols[headers.indexOf("Achievement")] ?? "None"),
        behaviour_id: studentBehavioursDescToIdMap.get(iCols[headers.indexOf("Behaviour")] ?? "None"),
        social_id: studentSocialsDescToIdMap.get(iCols[headers.indexOf("Social")] ?? "None"),
      } as StudentsNoNulls);
    }
  }
  return arr.filter(s => !!s.number);
};

const importTeachers = (hRow: string, rows: string[], delim: string): TeachersNoNulls[] => {
  const arr = new Array<TeachersNoNulls>();
  const headers = hRow.split(delim);
  for (const row of rows) {
    const iCols = row.split(delim);
    arr.push({
      prefix: iCols[headers.indexOf("Prefix")] ?? "",
      first: iCols[headers.indexOf("First")] ?? "",
      last: iCols[headers.indexOf("Last")] ?? "",
      years: Number(iCols[headers.indexOf("Years") ?? 0]),
      grade0am: (iCols[headers.indexOf("ECS-AM")] ?? "").toLowerCase() === "true",
      grade0pm: (iCols[headers.indexOf("ECS-PM")] ?? "").toLowerCase() === "true",
      grade1: (iCols[headers.indexOf("Grade 1")] ?? "").toLowerCase() === "true",
      grade2: (iCols[headers.indexOf("Grade 2")] ?? "").toLowerCase() === "true",
      grade3: (iCols[headers.indexOf("Grade 3")] ?? "").toLowerCase() === "true",
      grade4: (iCols[headers.indexOf("Grade 4")] ?? "").toLowerCase() === "true",
      grade5: (iCols[headers.indexOf("Grade 5")] ?? "").toLowerCase() === "true",
      grade6: (iCols[headers.indexOf("Grade 6")] ?? "").toLowerCase() === "true",
      grade7: (iCols[headers.indexOf("Grade 7")] ?? "").toLowerCase() === "true",
      grade8: (iCols[headers.indexOf("Grade 8")] ?? "").toLowerCase() === "true",
      grade9: (iCols[headers.indexOf("Grade 9")] ?? "").toLowerCase() === "true",
      grade10: (iCols[headers.indexOf("Grade 10")] ?? "").toLowerCase() === "true",
      grade11: (iCols[headers.indexOf("Grade 11")] ?? "").toLowerCase() === "true",
      grade12: (iCols[headers.indexOf("Grade 12")] ?? "").toLowerCase() === "true",
      grade13: (iCols[headers.indexOf("Grade 13")] ?? "").toLowerCase() === "true",
    } as TeachersNoNulls);
  }
  return arr.filter(s => !!s.last);
};

export const parseClassroomV = (sorted: VSortedNoNulls | SortedWithIntensities) =>
  ({
    id: sorted.c_id,
    classroom_name: sorted.c_name,
    seats0am: sorted.c_seats0am,
    seats0pm: sorted.c_seats0pm,
    seats1: sorted.c_seats1,
    seats2: sorted.c_seats2,
    seats3: sorted.c_seats3,
    seats4: sorted.c_seats4,
    seats5: sorted.c_seats5,
    seats6: sorted.c_seats6,
    seats7: sorted.c_seats7,
    seats8: sorted.c_seats8,
    seats9: sorted.c_seats9,
    seats10: sorted.c_seats10,
    seats11: sorted.c_seats11,
    seats12: sorted.c_seats12,
    seats13: sorted.c_seats13,
    teacher_id: sorted.t_id,
    prefix: sorted.t_prefix,
    last: sorted.t_last,
    years: sorted.t_years,
    grade0am: sorted.t_grade0am,
    grade0pm: sorted.t_grade0pm,
    grade1: sorted.t_grade1,
    grade2: sorted.t_grade2,
    grade3: sorted.t_grade3,
    grade4: sorted.t_grade4,
    grade5: sorted.t_grade5,
    grade6: sorted.t_grade6,
    grade7: sorted.t_grade7,
    grade8: sorted.t_grade8,
    grade9: sorted.t_grade9,
    grade10: sorted.t_grade10,
    grade11: sorted.t_grade11,
    grade12: sorted.t_grade12,
    grade13: sorted.t_grade13,
  }) as VClassroomsNoNulls;

export const parseCondition = (
  v_cond: VStudentConditionsNoNulls | VTeacherConditionsNoNulls,
): StudentConditionsNoNulls | TeacherConditionsNoNulls => ({
  id: v_cond.id,
  source_id: v_cond.s_id,
  definition_id: v_cond.def_id,
  target_id: v_cond.tgt_id,
  owner_id: v_cond.owner_id,
  overruled: v_cond.overruled,
});

export const parseStudentVSorted = (sorted: VSortedNoNulls | SortedWithIntensities) =>
  ({
    id: sorted.s_id,
    number: sorted.s_number,
    grade: sorted.s_grade,
    last: sorted.s_last,
    first: sorted.s_first,
    gender: sorted.s_gender,
    indigenous: sorted.s_indigenous,
    sa_id: sorted.sa_id,
    sa_desc: sorted.sa_desc,
    sb_id: sorted.sb_id,
    sb_desc: sorted.sb_desc,
    ss_id: sorted.ss_id,
    ss_desc: sorted.ss_desc,
    ell: sorted.s_ell,
    sp_ed_1: sorted.s_sp_ed_1,
    sp_ed_2: sorted.s_sp_ed_2,
    sp_ed_3: sorted.s_sp_ed_3,
    spcl_ed_1: sorted.s_spcl_ed_1,
    spcl_ed_2: sorted.s_spcl_ed_2,
  }) as VStudentsNoNulls;

export const parseStudentVFail = (failed: VFailedNoNulls) =>
  ({
    id: failed.s_id,
    number: failed.s_number,
    grade: failed.s_grade,
    last: failed.s_last,
    first: failed.s_first,
    gender: failed.s_gender,
    indigenous: failed.s_indigenous,
    sa_id: failed.s_sa_id,
    sa_desc: failed.s_sa_desc,
    sb_id: failed.s_sb_id,
    sb_desc: failed.s_sb_desc,
    ss_id: failed.s_ss_id,
    ss_desc: failed.s_ss_desc,
    ell: failed.s_ell,
    sp_ed_1: failed.s_sp_ed_1,
    sp_ed_2: failed.s_sp_ed_2,
    sp_ed_3: failed.s_sp_ed_3,
    spcl_ed_1: failed.s_spcl_ed_1,
    spcl_ed_2: failed.s_spcl_ed_2,
  }) as VStudentsNoNulls;

export const PSConflictIcon = (props: { size: "small" | "medium"; disabled?: boolean; onClick?: any }) => {
  const theme = useTheme();
  return (
    <IconButton {...props}>
      <ErrorOutlineIcon
        fontSize="inherit"
        sx={{ color: theme.palette.mode === "light" ? CRPS_ORANGE_LIGHT_BRIGHT : CRPS_ORANGE_DARK_BRIGHT }}
      />
    </IconButton>
  );
};

export const PSDataGrid = (props: {
  actionDeleteColName?: string;
  actionDeleteVisible: boolean;
  columns: GridColDef[];
  heightOffset?: number;
  initialSortField: "grade" | "last" | "name";
  loading: boolean;
  onProcessRowUpdateError: (error: Error) => void;
  processRowUpdate: (newRow: GridRowModel, oldRow: GridRowModel) => Promise<any>;
  psType: PsType;
  rows: ClassroomsNoNulls[] | StudentsNoNulls[] | TeachersNoNulls[];
  setActionDeleteVisible: Dispatch<SetStateAction<boolean>>;
  setAddDialogOpen: Dispatch<SetStateAction<boolean>>;
  setSnack: SetSnackType;
  teachers: TeachersNoNulls[];
  toolbarElementsToAdd?: JSX.Element[];
  toolbarHeaderRow?: JSX.Element;
  upload: (uploadedRows: Upload[]) => void;
}) => {
  const [storedFilter, setStoredFilter] = useLocalStorage(psLocalStorageFilter(props.psType), {
    items: [],
  } as GridFilterModel);

  const [filter, setFilter] = useState(storedFilter);

  const handleFilterModelChange = (newModel: GridFilterModel) => {
    setFilter(newModel);
    setStoredFilter(newModel);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: COMPONENT_CUSTOM_MAXHEIGHT(props.heightOffset ?? 0),
        width: "100%",
      }}
    >
      <DataGrid
        autoPageSize
        columns={props.columns}
        columnVisibilityModel={{
          [props.actionDeleteColName ?? "actionDelete"]: props.actionDeleteVisible,
        }}
        density="compact"
        disableRowSelectionOnClick
        filterModel={filter}
        hideFooterPagination // hide bottom-right pagination
        ignoreDiacritics
        initialState={{
          sorting: { sortModel: [{ field: props.initialSortField, sort: "asc" }] },
        }}
        loading={props.loading}
        onFilterModelChange={handleFilterModelChange}
        onProcessRowUpdateError={props.onProcessRowUpdateError}
        processRowUpdate={props.processRowUpdate}
        rowHeight={39}
        rows={props.rows}
        slots={{
          loadingOverlay: LinearProgress as any,
          toolbar: () => (
            <Box>
              <Box sx={{ display: "flex", justifyContent: "flex-start", width: "fit-content" }}>
                {/* show top-left pagination */}
                <GridPagination rowsPerPageOptions={[]} showFirstButton showLastButton />
              </Box>
              <Stack direction="row" spacing={1}>
                <ButtonTrashRow
                  disabled={props.rows.length < 1}
                  toggleVisibility={props.setActionDeleteVisible}
                  visible={props.actionDeleteVisible}
                />
                <Button startIcon={<AddIcon />} onClick={() => props.setAddDialogOpen(true)}>
                  Add {props.psType.endsWith("s") ? props.psType.slice(0, props.psType.length - 1) : props.psType}
                </Button>
                {props.psType !== "Students" ? (
                  <></>
                ) : (
                  <ButtonImportFile
                    extension={".text"}
                    invalid={(reason: string) => props.setSnack(reason, "error")}
                    psType={props.psType}
                    teachers={props.teachers}
                    uploaded={props.upload}
                  />
                )}
                <ButtonImportFile
                  extension={".csv"}
                  invalid={(reason: string) => props.setSnack(reason, "error")}
                  psType={props.psType}
                  teachers={props.teachers}
                  uploaded={props.upload}
                />
                <ButtonExportCSV data={props.rows} psType={props.psType} teachers={props.teachers} />
                {props.toolbarElementsToAdd?.map((el, i) => (
                  <span key={i}>{el}</span>
                ))}
              </Stack>
              {props.toolbarHeaderRow}
            </Box>
          ),
        }}
      />
    </Box>
  );
};

export const PSTooltip = (props: { child: JSX.Element; title: string }) => (
  <Tooltip
    arrow
    leaveDelay={10}
    placement="top"
    slotProps={{
      popper: {
        modifiers: [
          {
            name: "offset",
            options: { offset: [0, -14] },
          },
        ],
        sx: {
          "& .MuiTooltip-tooltip": {
            fontSize: "0.9rem",
          },
        },
      },
    }}
    title={props.title ?? ""}
  >
    {props.child}
  </Tooltip>
);

export const PSCondNoStuSelected = () => (
  <Box
    sx={{
      alignItems: "center",
      display: "flex",
      height: "32px",
      justifyContent: "center",
      pr: 50,
    }}
  >
    <Typography variant="body2" color="text.secondary">
      No Student Selected
    </Typography>
  </Box>
);

export const schoolGrades = (grades: SettingsGrades) =>
  Object.keys(grades)
    .filter(g => !!grades[g as keyof SettingsGrades])
    .toSorted((a, b) => GRADES.indexOf(a) - GRADES.indexOf(b));

export const totalSeats = (classroom: ClassroomsNoNulls | VClassroomsNoNulls) =>
  classroom.seats0am +
  classroom.seats0pm +
  classroom.seats1 +
  classroom.seats2 +
  classroom.seats3 +
  classroom.seats4 +
  classroom.seats5 +
  classroom.seats6 +
  classroom.seats7 +
  classroom.seats8 +
  classroom.seats9 +
  classroom.seats10 +
  classroom.seats11 +
  classroom.seats12 +
  classroom.seats13;

export const totalSeatsOfGrade = (classroom: ClassroomsNoNulls | VClassroomsNoNulls, grade: Grade) =>
  classroom[
    `seats${grade.startsWith("ECS") ? "0" : ""}${grade.slice(-2).trim().toLowerCase()}` as keyof (
      ClassroomsNoNulls | VClassroomsNoNulls
    )
  ];

export const timeout = (msDelay: number) => new Promise<void>(p => setTimeout(p, msDelay));

export const upload = (
  psType: PsType | "PowerSchoolText",
  fileContent: typeof FileReader.prototype.result,
  teachers: TeachersNoNulls[],
): {
  isValid: boolean;
  reason?: string;
  uploadedRows?: any[];
} => {
  let isValid: boolean;
  let reason: string;
  let uploadedRows: any[];
  const [headersRow, ...rows] = (fileContent as string).split(/[\r\n]+/);
  const delim = psType === "PowerSchoolText" ? "\t" : ",";
  switch (psType) {
    case "Classrooms":
      ({ isValid, reason = "" } = validateHeaders(UPLOAD_COLUMNS_CLASSROOMS, headersRow, delim));
      if (!isValid) return { isValid, reason };
      uploadedRows = importClassrooms(headersRow, rows, delim, teachers);
      break;
    case "PowerSchoolText":
      ({ isValid, reason = "" } = validateHeaders(UPLOAD_COLUMNS_PSCHOOL_STUDENTS, headersRow, delim));
      if (!isValid) return { isValid, reason };
      uploadedRows = importStudents(headersRow, rows, delim);
      break;
    case "Students":
      ({ isValid, reason = "" } = validateHeaders(UPLOAD_COLUMNS_STUDENTS, headersRow, delim));
      if (!isValid) return { isValid, reason };
      uploadedRows = importStudents(headersRow, rows, delim);
      break;
    case "Teachers":
      ({ isValid, reason = "" } = validateHeaders(UPLOAD_COLUMNS_TEACHERS, headersRow, delim));
      if (!isValid) return { isValid, reason };
      uploadedRows = importTeachers(headersRow, rows, delim);
      break;
    default:
      isValid = false;
      reason = `unimplemented 'validateUpload()' type "${psType}"`;
      uploadedRows = [];
      console.error("upload error:", reason);
  }
  if (!isValid) return { isValid, reason };
  return { isValid: true, uploadedRows };
};

export const UploadOverwriteDialog = (props: {
  open: boolean;
  upload: (overwrite: boolean) => void;
  uploadCancel: () => void;
}) => (
  <Dialog open={props.open}>
    <DialogTitle>Overwrite?</DialogTitle>
    <DialogContent>
      <DialogContentText>
        Would you like to completely overwrite all entries, as a fresh import, erasing the existing entries?
        <br />
        <br />
        If you{" "}
        <strong>
          choose <em>not</em> to
        </strong>
        , the system will attempt to append the file, but will fail if there are duplicates of existing records.
        <br />
        <br />
        If you <strong>choose to</strong>, all existing entries will be purged.
      </DialogContentText>
    </DialogContent>
    <DialogActions>
      <Button onClick={() => props.uploadCancel()}>Cancel</Button>
      <Button onClick={() => props.upload(false)} variant="outlined">
        No, append only
      </Button>
      <Button onClick={() => props.upload(true)} variant="contained">
        Yes, replace all
      </Button>
    </DialogActions>
  </Dialog>
);

const validateHeaders = (
  columnNames: string[],
  headerRow: string,
  delimiter: "," | "\t",
): { isValid: boolean; reason?: string } => {
  const headers = headerRow.split(delimiter);
  for (const h of columnNames) {
    if (!headers.includes(h)) {
      // if "Sched_NextYearGrade" is missing then must "Grade_Level"++
      if (delimiter === "\t" && h === "Sched_NextYearGrade" && headers.includes("Grade_Level")) continue;

      // if "State_StudentNumber" is missing then "Student_Number"
      if (delimiter === "\t" && h === "State_StudentNumber" && headers.includes("Student_Number")) continue;

      return { isValid: false, reason: `Missing header "${h}"` };
    }
  }
  return { isValid: true };
};

const yMd_Hms = () => new Date().toISOString().slice(0, 19).replaceAll("-", "").replaceAll(":", "").replace("T", "_");
